import { useEffect, useState } from "react";
import {
  TextField,
  Grid,
  Button,
  Typography,
  Alert,
  Snackbar,
  Box,
  Stepper,
  Step,
  StepLabel,
  Divider,
  DialogActions,
  InputAdornment,
  Skeleton,
  Chip,
  Link,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import productsService from "../../../services/productsService";
import CircularProgress from "@mui/material/CircularProgress";

const initialAlert = { severity: "success", message: "" };

const MultiBultoModal = ({
  handleCreateProducto,
  productSKU,
  onClose,
  secondaryLabel = "Cancelar",
  onSecondary,
  showBackIcon = false,
}) => {
  const [cantidadBultos, setCantidadBultos] = useState();
  const [currentStep, setCurrentStep] = useState(0);
  const [bultoPrincipal, setBultoPrincipal] = useState({});
  const [bultosData, setBultosData] = useState([]);
  const [alertObj, setAlertObj] = useState(initialAlert);
  const [openAlert, setOpenAlert] = useState(false);
  const user_id = sessionStorage.getItem("User");
  const [errorCantidadBultos, setErrorCantidadBultos] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);

  useEffect(() => {
    const fetchProductCombo = async () => {
      setLoadingSpinner(true);
      try {
        const res = await productsService.getProductsBySku(productSKU);
        const productData = Array.isArray(res.data) ? res.data : [res.data];

        if (!productData.length) return;

        const internalBultos = productData.filter((item) => !item.is_multipack);
        const principal = productData.find((item) => item.is_multipack) || {};

        setBultoPrincipal({
          id: principal.id || null,
          sku: principal.sku_id || "",
          ean: principal.ean_id || "",
          nombre: principal.name || "",
          alto: principal.height || 0,
          largo: principal.deep || 0,
          ancho: principal.width || 0,
          peso: principal.weight || 0,
          observaciones: principal.observation || "",
        });

        const formattedBultos = internalBultos.map((item) => ({
          id: item.id || null,
          sku: item.sku_id || "",
          ean: item.ean_id || "",
          nombre: item.name || "",
          alto: item.height || 0,
          largo: item.deep || 0,
          ancho: item.width || 0,
          peso: item.weight || 0,
          observaciones: item.observation || "",
        }));

        setBultosData(formattedBultos);
        setCantidadBultos(internalBultos.length || 2);
        setCurrentStep(0);
      } catch (error) {
        console.error("Error al cargar los datos del producto:", error);
      } finally {
        setLoadingSpinner(false);
      }
    };

    if (productSKU) {
      fetchProductCombo();
    }
  }, [productSKU]);

  useEffect(() => {
    if (bultoPrincipal?.ean && currentStep > 0) {
      setBultosData((prevData) => {
        const updatedData = [...prevData];
        updatedData[currentStep - 1] = {
          ...updatedData[currentStep - 1],
          ean: bultoPrincipal.ean,
        };
        return updatedData;
      });
    }
  }, [bultoPrincipal, currentStep]);

  const handlePrincipalChange = (field, value) => {
    setBultoPrincipal({ ...bultoPrincipal, [field]: value });
  };

  const handleBultoChange = (field, value) => {
    const newBultosData = [...bultosData];
    newBultosData[currentStep - 1] = {
      ...newBultosData[currentStep - 1],
      [field]: value,
    };
    setBultosData(newBultosData);
  };

  const handleNext = () => {
    if (currentStep === 0 && bultosData.length === 0) {
      setBultosData(
        Array.from({ length: cantidadBultos }, () => ({
          sku: "",
          ean: "",
          nombre: "",
          peso: "",
          ancho: "",
          largo: "",
          alto: "",
          observaciones: "",
        }))
      );
    }
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenAlert(false);
  };

  const handleSave = async () => {
    setLoadingButton(true);
    const bultosInternos = bultosData.map((bulto) => ({
      ...(productSKU && bulto.id ? { id: bulto.id } : {}),
      sku_id: bulto.sku,
      ean_id: bulto.ean,
      name: bulto.nombre || "",
      height: Number(bulto.alto || 0),
      deep: Number(bulto.largo || 0),
      width: Number(bulto.ancho || 0),
      weight: Number(bulto.peso || 0),
      observation: bulto.observaciones || "",
      user_id: Number(user_id),
      is_multipack: false,
      active: true,
    }));

    const bultoPrincipalObj = {
      ...(productSKU && bultoPrincipal.id ? { id: bultoPrincipal.id } : {}),
      sku_id: bultoPrincipal.sku,
      ean_id: bultoPrincipal.ean || "",
      name: bultoPrincipal.nombre || "",
      height: Number(bultoPrincipal.alto || 0),
      deep: Number(bultoPrincipal.largo || 0),
      width: Number(bultoPrincipal.ancho || 0),
      weight: Number(bultoPrincipal.peso || 0),
      observation: bultoPrincipal.observaciones || "",
      user_id: Number(user_id),
      is_multipack: true,
      active: true,
    };

    const allBultos = [bultoPrincipalObj, ...bultosInternos];

    try {
      if (productSKU) {
        let newProduct =
          await productsService.updateProductMultiPack(allBultos);
        if (newProduct?.data) {
          setAlertObj({
            severity: "success",
            message: "Producto editado exitosamente",
          });
          setOpenAlert(true);
          setTimeout(() => {
            onClose();
          }, 1000);
        }
      } else {
        let newProduct = await productsService.postProductMultiPack(allBultos);
        if (newProduct?.data) {
          setAlertObj({
            severity: "success",
            message: "Producto guardado exitosamente",
          });
          setOpenAlert(true);
          setTimeout(() => {
            onClose();
          }, 1000);
        }
      }
      setTimeout(() => {
        handleCreateProducto();
      }, 1500);
    } catch (error) {
      setAlertObj({
        severity: "error",
        message: "Ocurrio un error al guardar el producto",
      });
      setOpenAlert(true);
    } finally {
      setLoadingButton(false);
    }
  };

  function generateHash(length = 10) {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let hash = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      hash += characters[randomIndex];
    }
    return hash;
  }

  const handleGenerateHash = () => {
    const newHash = generateHash(10);
    handleBultoChange("sku", newHash);
  };

  const isStepValid = () => {
    if (currentStep === 0) {
      return (
        cantidadBultos > 1 &&
        bultoPrincipal.sku?.trim() &&
        bultoPrincipal.ean?.trim() &&
        bultoPrincipal.nombre?.trim()
      );
    } else {
      const currentBulto = bultosData[currentStep - 1];
      return (
        currentBulto?.sku?.trim() &&
        currentBulto?.nombre?.trim() &&
        currentBulto?.peso > 0 &&
        currentBulto?.ancho > 0 &&
        currentBulto?.largo > 0 &&
        currentBulto?.alto > 0
      );
    }
  };

  const handleCantidadBultosChange = (e) => {
    const value = e.target.value;

    if (/^\d*$/.test(value)) {
      const numericValue = parseInt(value, 10);

      if (numericValue < 2) {
        setErrorCantidadBultos("La cantidad minima es 2");
      } else {
        setErrorCantidadBultos("");
      }

      setCantidadBultos(numericValue || "");
    }
  };

  const totalSteps = cantidadBultos ? cantidadBultos + 1 : 1;

  if (loadingSpinner) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={56} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      {/* Stepper */}
      {cantidadBultos > 1 && (
        <Stepper
          activeStep={currentStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepLabel-label": { fontSize: "0.75rem", mt: 0.5 },
            "& .MuiStepConnector-line": { borderColor: "grey.300" },
          }}
        >
          <Step>
            <StepLabel>General</StepLabel>
          </Step>
          {Array.from({ length: cantidadBultos || 0 }, (_, i) => (
            <Step key={i}>
              <StepLabel>Bulto {i + 1}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Step 0: Info general */}
      {currentStep === 0 && (
        <>
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: "block", mb: 1.5 }}
          >
            Informacion general del multibulto
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Cantidad de bultos"
                placeholder="Ej: 3"
                variant="outlined"
                size="small"
                fullWidth
                required
                InputLabelProps={{
                  shrink: Boolean(cantidadBultos),
                }}
                value={cantidadBultos || ""}
                onChange={handleCantidadBultosChange}
                error={!!errorCantidadBultos}
                helperText={errorCantidadBultos || "Minimo 2 bultos"}
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SKU"
                placeholder="Ej: MB-001"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultoPrincipal.sku || ""}
                onChange={(e) => {
                  const inputValue = e.target.value.toUpperCase();
                  handlePrincipalChange("sku", inputValue);
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="EAN"
                placeholder="Ej: 7790001234567"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultoPrincipal.ean || ""}
                onChange={(e) => {
                  const regex = /^[a-zA-Z0-9]*$/;
                  const inputValue = e.target.value.toUpperCase();
                  if (inputValue.length > 14) return;
                  if (!regex.test(inputValue)) return;
                  handlePrincipalChange("ean", inputValue);
                }}
                helperText="Maximo 14 caracteres"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre"
                placeholder="Ej: MACETA GRANDE"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultoPrincipal.nombre || ""}
                onChange={(e) => {
                  const inputValue = e.target.value.toUpperCase();
                  handlePrincipalChange("nombre", inputValue);
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                placeholder="Notas internas sobre el producto..."
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={bultoPrincipal.observaciones || ""}
                onChange={(e) =>
                  handlePrincipalChange("observaciones", e.target.value)
                }
                inputProps={{ maxLength: 160 }}
                helperText={`${(bultoPrincipal.observaciones || "").length}/160 caracteres`}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Steps 1+: Cada bulto */}
      {currentStep > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
            >
              Bulto {currentStep} de {cantidadBultos}
            </Typography>
            <Chip
              label={`EAN heredado: ${bultoPrincipal.ean || "—"}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500, fontSize: "0.7rem" }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Codigo de bulto"
                placeholder="Genera o ingresa un codigo"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.sku || ""}
                onChange={(e) => handleBultoChange("sku", e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Link
                        component="button"
                        variant="caption"
                        onClick={handleGenerateHash}
                        underline="hover"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <AutorenewIcon sx={{ fontSize: 14 }} />
                        Generar
                      </Link>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre"
                placeholder="Ej: BULTO 1 - MACETA"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.nombre || ""}
                onChange={(e) => handleBultoChange("nombre", e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Peso"
                placeholder="0"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.peso || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleBultoChange("peso", value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">g</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="EAN"
                variant="outlined"
                size="small"
                fullWidth
                value={bultoPrincipal?.ean || ""}
                disabled
                helperText="Se hereda del producto principal"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: "block", mb: 1.5 }}
          >
            Dimensiones del bulto
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Alto"
                placeholder="0"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.alto || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleBultoChange("alto", value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">cm</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Largo"
                placeholder="0"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.largo || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleBultoChange("largo", value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">cm</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Ancho"
                placeholder="0"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={bultosData[currentStep - 1]?.ancho || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleBultoChange("ancho", value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="text.secondary">cm</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ inputMode: "numeric" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observaciones"
                placeholder="Notas sobre este bulto..."
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={bultosData[currentStep - 1]?.observaciones || ""}
                onChange={(e) =>
                  handleBultoChange("observaciones", e.target.value)
                }
                inputProps={{ maxLength: 160 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Footer actions */}
      <DialogActions sx={{ px: 0, pt: 2.5, pb: 0, justifyContent: "space-between" }}>
        <Box>
          {currentStep === 0 ? (
            <Button
              variant="outlined"
              onClick={onSecondary || onClose}
              startIcon={showBackIcon ? <ArrowBackIcon fontSize="small" /> : null}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                borderColor: "#cbd5e1",
                color: "#475569",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
              }}
            >
              {secondaryLabel}
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<ArrowBackIcon fontSize="small" />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                borderColor: "#cbd5e1",
                color: "#475569",
                "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f8fafc" },
              }}
            >
              Volver
            </Button>
          )}
        </Box>
        <Box>
          {currentStep !== cantidadBultos ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid()}
              endIcon={<ArrowForwardIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                px: 3,
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isStepValid() || loadingButton}
              startIcon={
                loadingButton ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  <SaveOutlinedIcon fontSize="small" />
                )
              }
              sx={{
                textTransform: "none",
                px: 3,
                borderRadius: "8px",
                fontWeight: 600,
              }}
            >
              {loadingButton
                ? productSKU
                  ? "Guardando..."
                  : "Creando..."
                : productSKU
                  ? "Guardar cambios"
                  : "Crear multibulto"}
            </Button>
          )}
        </Box>
      </DialogActions>

      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleClose}
          severity={alertObj?.severity}
          sx={{ width: "100%" }}
        >
          {alertObj.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MultiBultoModal;
